import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { PullToRefresh } from '@/pages/(app-webview)/components/PullToRefresh';
import { WebviewRefreshContext } from '@/pages/(app-webview)/hooks/useWebviewRefresh';

interface WebviewLayoutProps {
  children: React.ReactNode;
}

export function WebviewLayout({ children }: WebviewLayoutProps) {
  // Webview 확대/축소 방지
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const original = viewport?.getAttribute('content') ?? '';
    viewport?.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );
    return () => {
      if (original) viewport?.setAttribute('content', original);
    };
  }, []);

  // ── PullToRefresh 상태 관리 ──
  const handlerRef = useRef<(() => Promise<void>) | null>(null);
  const [refreshDisabled, setRefreshDisabled] = useState(false);

  const setHandler = useCallback((handler: () => Promise<void>) => {
    handlerRef.current = handler;
  }, []);

  const clearHandler = useCallback(() => {
    handlerRef.current = null;
  }, []);

  const setDisabled = useCallback((disabled: boolean) => {
    setRefreshDisabled(disabled);
  }, []);

  const disabledRef = useRef(false);
  disabledRef.current = refreshDisabled;

  const contextValue = useMemo(
    () => ({ setHandler, clearHandler, setDisabled, handlerRef, disabledRef }),
    [setHandler, clearHandler, setDisabled],
  );

  const handleRefresh = useCallback(async () => {
    if (handlerRef.current) {
      await handlerRef.current();
    }
  }, []);

  return (
    <WebviewRefreshContext.Provider value={contextValue}>
      <div
        className="flex h-dvh flex-col select-none bg-white dark:bg-neutral-950"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <main className="mx-auto flex w-full max-w-lg flex-1 flex-col overflow-hidden px-4">
          <PullToRefresh
            onRefresh={handleRefresh}
            disabled={refreshDisabled}
          >
            {children}
          </PullToRefresh>
        </main>
        <Toaster
          containerClassName="!inset-0 !w-full !left-0 !top-1.5"
          toastOptions={{
            duration: 2000,
            className:
              '!w-full !m-0 px-4 py-2 !bg-black/70 !rounded-xl !backdrop-blur-sm',
          }}
        />
      </div>
    </WebviewRefreshContext.Provider>
  );
}
